import { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import type { DocNode } from '@atlaskit/adf-schema/doc';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type { ConnectionSectionsData } from '../teamwork-graph-preview-panel/sections/useConnectionSectionsData.ts';
import type { BylineContentType } from '../utils/contentAri.ts';

import type {
	AtAGlanceStructuralAncestor,
	AiFeatureErrorResponse,
	AtAGlanceOutput,
} from './atAGlanceTypes.ts';
import {
	type AtAGlanceFailureReason,
	type AtAGlanceRecoveryAttributes,
	getServiceErrorCodeForAnalytics,
	getAtAGlanceAbortedEvent,
	getAtAGlanceFailedEvent,
	getAtAGlanceSucceededEvent,
} from './atAGlanceAnalytics.ts';
import { buildAtAGlanceInput } from './buildAtAGlanceInput.ts';
import { parseAtAGlanceOutput } from './parseAtAGlanceOutput.ts';
import {
	type AtAGlanceRetryReason,
	getServiceRetryReason,
	getQuotaCooldown,
	recordQuotaCooldown,
	clearQuotaCooldownsForTesting,
	getRetryAfterDelay,
	scheduleAfter,
} from './atAGlanceRetry.ts';

const ENDPOINT = '/gateway/api/ai/v2/ai-feature/twg-hub-summaries';
const EXPERIENCE_ID = 'confluence-twg-hub-summaries';
const REQUEST_TIMEOUT_MS = 60_000;
const RECOVERY_BUDGET_MS = 120_000;

class AtAGlanceTimeoutError extends Error {
	constructor() {
		super('At a Glance recovery timed out');
		this.name = 'AtAGlanceTimeoutError';
	}
}

type AtAGlanceResultState = {
	contentId: string;
	output: AtAGlanceOutput | null;
	error: Error | null;
	retryPending?: boolean;
	retryReason?: AtAGlanceRetryReason;
};

const createSingleEntryCache = <TValue>(): {
	get: (key: string) => TValue | undefined;
	set: (key: string, value: TValue) => void;
	clear: () => void;
} => {
	let entry: { key: string; value: TValue } | undefined;

	return {
		get: (key) => (entry?.key === key ? entry.value : undefined),
		set: (key, value) => {
			entry = { key, value };
		},
		clear: () => {
			entry = undefined;
		},
	};
};

// Reuse only the most recent successful response. This survives panel/modal
// remounts without accumulating one entry per page, and the exact request body
// makes every input that can affect the summaries part of cache identity.
// This intentionally has no TTL: backend-only prompt or model changes are not
// observed until another success replaces the entry or the page reloads. That
// trade-off is acceptable because this cache targets immediate consecutive opens.
const resultCache = createSingleEntryCache<AtAGlanceOutput>();

export const clearAtAGlanceResultCacheForTesting = (): void => {
	resultCache.clear();
	clearQuotaCooldownsForTesting();
};

export type UseAtAGlanceOptions = {
	enabled: boolean;
	contentId?: string;
	/** Selects the `content_ari` the request names. Undefined means page. */
	contentType?: BylineContentType;
	connections: Pick<ConnectionSectionsData, 'appearsIn' | 'linksTo' | 'relatedContent'>;
	pageAdf: DocNode | null;
	pageVersion?: number | null;
	pageUpdatedAt?: string | null;
	structuralAncestors?: readonly AtAGlanceStructuralAncestor[] | null;
	outgoingLinksLoading?: boolean;
	/** Undefined while the title query is pending; null when it resolved unavailable. */
	pageTitle?: string | null;
};

export type UseAtAGlanceResult = {
	output: AtAGlanceOutput | null;
	/**
	 * True while we are still waiting on prerequisites or the cc-smarts response.
	 * False once we have output, have an error, or know we will not fetch (ineligible).
	 */
	loading: boolean;
	error: Error | null;
	retryPending?: boolean;
	retryReason?: AtAGlanceRetryReason;
};

const isAbortError = (error: unknown): boolean =>
	typeof error === 'object' &&
	error !== null &&
	'name' in error &&
	(error as { name: string }).name === 'AbortError';

const toError = (error: unknown): Error =>
	error instanceof Error ? error : new Error(String(error));

const isAiFeatureErrorResponse = (value: unknown): value is AiFeatureErrorResponse => {
	if (!value || typeof value !== 'object') {
		return false;
	}

	const error = (value as { error?: unknown }).error;
	if (!error || typeof error !== 'object') {
		return false;
	}

	const candidate = error as Record<string, unknown>;
	return (
		typeof candidate.code === 'string' &&
		typeof candidate.message === 'string' &&
		typeof candidate.retryable === 'boolean' &&
		typeof candidate.requestId === 'string'
	);
};

/**
 * Retains the cc-smarts error envelope for diagnostics and retry classification while
 * keeping the surfaced Error message generic. Recognized transient failures support one delayed
 * automatic retry after the server cooldown.
 */
export class AtAGlanceRequestError extends Error {
	readonly status: number;
	readonly code: string | undefined;
	readonly retryable: boolean | undefined;
	readonly requestId: string | undefined;
	readonly serviceMessage: string | undefined;
	readonly retryAfterMs: number;
	readonly retryReason: AtAGlanceRetryReason | undefined;

	constructor(status: number, responseBody: unknown, retryAfter: string | null = null) {
		super(`At a Glance request failed with HTTP ${status}`);
		this.name = 'AtAGlanceRequestError';
		this.status = status;

		if (isAiFeatureErrorResponse(responseBody)) {
			this.code = responseBody.error.code;
			this.retryable = responseBody.error.retryable;
			this.requestId = responseBody.error.requestId;
			this.serviceMessage = responseBody.error.message;
		}
		this.retryReason = getServiceRetryReason(status, this.code, this.retryable);
		this.retryAfterMs = getRetryAfterDelay(
			retryAfter,
			this.retryReason === 'transientServiceError' ? 2_000 : undefined,
		);
	}
}

/**
 * Asks cc-smarts for the Teamwork Graph "at a glance" overview and per-link
 * summaries. When enabled, waits for Appears in, Links to, and enabled Related
 * content to settle before the first request.
 *
 * The byline enables requests on the first panel open for the current page. Its
 * dedicated bridge shares the result with the menu, panel and modal. Closing or
 * reopening those surfaces does not cancel or restart work.
 *
 * Failures are reported and returned as `error` — the rest of the connections UI
 * must not depend on this response. Overview bullets are rendered by
 * `AtAGlanceSection`; the per-link one-liners are rendered on Related content
 * rows.
 *
 * Each logical request reports one terminal operational event —
 * succeeded, failed (with a categorised reason), or aborted (torn down in flight).
 * A cache hit fires nothing: no request was made.
 */
export const useAtAGlance = ({
	enabled,
	contentId,
	contentType,
	connections,
	pageTitle,
	pageAdf,
	pageVersion,
	pageUpdatedAt,
	structuralAncestors,
	outgoingLinksLoading = false,
}: UseAtAGlanceOptions): UseAtAGlanceResult => {
	const { cloudId } = useSessionData();
	const { locale } = useIntl();
	const { createEvent } = useConfluenceAnalyticsEvents();
	const [resultState, setResultState] = useState<AtAGlanceResultState | null>(null);
	const relatedContentLoading = Boolean(
		connections.relatedContent.enabled && connections.relatedContent.loading,
	);

	const requestBody = useMemo(() => {
		if (
			!enabled ||
			!contentId ||
			!cloudId ||
			!pageAdf ||
			typeof pageTitle !== 'string' ||
			connections.appearsIn.loading ||
			outgoingLinksLoading ||
			relatedContentLoading
		) {
			return null;
		}

		const input = buildAtAGlanceInput({
			cloudId,
			contentId,
			contentType,
			locale,
			pageTitle,
			pageVersion,
			pageUpdatedAt,
			pageAdf,
			structuralAncestors,
			incomingLinks: connections.appearsIn.links,
			outgoingLinks: connections.linksTo.links,
			relatedContentLinks: connections.relatedContent.links,
		});
		if (!input) {
			return null;
		}

		return JSON.stringify({ ai_feature_input: input });
	}, [
		enabled,
		pageAdf,
		pageTitle,
		pageUpdatedAt,
		pageVersion,
		structuralAncestors,
		cloudId,
		connections.appearsIn.links,
		connections.appearsIn.loading,
		outgoingLinksLoading,
		relatedContentLoading,
		connections.linksTo.links,
		connections.relatedContent.links,
		contentId,
		contentType,
		locale,
	]);
	const cachedOutput = requestBody ? (resultCache.get(requestBody) ?? null) : null;
	const currentResultState = resultState?.contentId === contentId ? resultState : null;
	const output = cachedOutput ?? currentResultState?.output ?? null;
	const error = cachedOutput ? null : (currentResultState?.error ?? null);

	// Raw link counts for analytics, in a ref so they never drive the effect: the
	// payload filters and caps these links, so the counts can change while the
	// request body does not. The effect snapshots them when it fires.
	const linkCountsRef = useRef({ incomingLinkCount: 0, outgoingLinkCount: 0 });
	linkCountsRef.current = {
		incomingLinkCount: connections.appearsIn.links.length,
		outgoingLinkCount: connections.linksTo.links.length,
	};

	useEffect(() => {
		if (!requestBody || !cloudId || !contentId) {
			setResultState((current) =>
				current
					? {
							contentId: current.contentId,
							output: current.output,
							error: current.error,
						}
					: null,
			);
			return;
		}
		const cachedResult = resultCache.get(requestBody);
		if (cachedResult) {
			// Keep this mounted consumer stable if another page later replaces the
			// single module-level cache entry. A cache hit is not a request, so no
			// operational event fires here.
			setResultState({ contentId, output: cachedResult, error: null });
			return;
		}

		const controller = new AbortController();
		const startedAt = performance.now();
		const { incomingLinkCount, outgoingLinkCount } = linkCountsRef.current;
		let cancelled = false;
		let settled = false;
		let cancelRetryTimer: (() => void) | undefined;
		let requestTimer: ReturnType<typeof setTimeout> | undefined;
		let recoveryTimer: ReturnType<typeof setTimeout> | undefined;
		const clearRequestTimers = () => {
			clearTimeout(requestTimer);
			clearTimeout(recoveryTimer);
			cancelRetryTimer?.();
		};
		let retryCount = 0;
		let retryReason: AtAGlanceRetryReason | undefined;
		let serviceErrorCode: string | undefined;
		let retryDelayMs = 0;
		const recoveryAttributes = (
			recoveryOutcome: AtAGlanceRecoveryAttributes['recoveryOutcome'],
		): AtAGlanceRecoveryAttributes => ({
			...(serviceErrorCode ? { serviceErrorCode } : {}),
			...(retryReason ? { retryCount, retryReason, retryDelayMs, recoveryOutcome } : {}),
		});
		setResultState((current) => ({
			contentId,
			output: current && current.contentId === contentId ? current.output : null,
			error: null,
		}));

		// Deliberately preserve the current output: hold the bullets we have while a refetch is in
		// flight. `requestBody` changes whenever the links do, and one response now feeds both
		// the panel and the modal — so clearing here would blank both surfaces back to
		// skeletons the moment a reader pressed "show more" on "Appears in", and make them wait
		// out a fresh LLM round trip for an answer already on screen. `loading` stays false for
		// the same reason (see below).

		const elapsedMs = () => Math.round(performance.now() - startedAt);

		const reportFailure = (reason: AtAGlanceFailureReason, failure: Error, statusCode?: number) => {
			if (cancelled || settled) {
				return;
			}
			settled = true;
			clearRequestTimers();
			setResultState((current) => ({
				contentId,
				output: current && current.contentId === contentId ? current.output : null,
				error: failure,
			}));
			getMonitoringClient().submitError(failure, { attribution: Attribution.INTEGRATIONS });
			createEvent(
				getAtAGlanceFailedEvent({
					reason,
					statusCode,
					incomingLinkCount,
					outgoingLinkCount,
					durationMs: elapsedMs(),
					...recoveryAttributes('exhausted'),
				}),
			).fire();
		};

		const setRetryState = (retryPending: boolean) => {
			setResultState((current) => ({
				contentId,
				output: current?.contentId === contentId ? current.output : null,
				error: retryPending ? null : (current?.error ?? null),
				retryPending,
				retryReason: retryPending ? retryReason : undefined,
			}));
		};

		const onTimeout = () => {
			reportFailure('timeout', new AtAGlanceTimeoutError());
			controller.abort();
		};
		recoveryTimer = setTimeout(onTimeout, RECOVERY_BUDGET_MS);

		const runRequest = (allowAutomaticRetry: boolean) => {
			if (cancelled) {
				return;
			}
			const quotaNotBefore = getQuotaCooldown(cloudId);
			if (quotaNotBefore > Date.now()) {
				retryReason = 'rateLimited';
				serviceErrorCode = 'AI_FEATURE_RATE_LIMITED';
				retryDelayMs += quotaNotBefore - Date.now();
				setRetryState(true);
				cancelRetryTimer = scheduleAfter(quotaNotBefore, () => runRequest(allowAutomaticRetry));
				return;
			}
			if (!allowAutomaticRetry) retryCount = 1;
			setRetryState(false);
			requestTimer = setTimeout(onTimeout, REQUEST_TIMEOUT_MS);
			fetch(ENDPOINT, {
				method: 'POST',
				credentials: 'include',
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json',
					'ATL-CloudId': cloudId,
					'x-experience-id': EXPERIENCE_ID,
					'x-product': 'confluence',
				},
				body: requestBody,
				signal: controller.signal,
			})
				.then(async (response) => {
					const responseBody: unknown = await response.json().catch(() => null);
					clearTimeout(requestTimer);
					if (cancelled || settled) {
						return;
					}
					if (!response.ok) {
						throw new AtAGlanceRequestError(
							response.status,
							responseBody,
							response.headers.get('Retry-After'),
						);
					}
					const parsed = parseAtAGlanceOutput(responseBody);
					if (!parsed) {
						reportFailure(
							'malformedResponse',
							new Error('At a Glance response envelope is malformed'),
						);
						return;
					}
					settled = true;
					clearRequestTimers();
					resultCache.set(requestBody, parsed);
					setResultState({ contentId, output: parsed, error: null });
					createEvent(
						getAtAGlanceSucceededEvent({
							overviewCount: parsed.overview.length,
							summariesCount: parsed.summaries.length,
							incomingLinkCount,
							outgoingLinkCount,
							durationMs: elapsedMs(),
							...recoveryAttributes('recovered'),
						}),
					).fire();
				})
				.catch((caught: unknown) => {
					clearTimeout(requestTimer);
					if (cancelled || settled || isAbortError(caught)) {
						return;
					}
					const requestError = caught instanceof AtAGlanceRequestError ? caught : undefined;
					serviceErrorCode = getServiceErrorCodeForAnalytics(requestError?.code);
					const rateLimited = requestError?.retryReason === 'rateLimited';
					const retryNotBefore = requestError?.retryReason
						? Date.now() + requestError.retryAfterMs + 250 + Math.floor(Math.random() * 1000)
						: 0;
					if (rateLimited) recordQuotaCooldown(cloudId, retryNotBefore);
					if (requestError?.retryReason && allowAutomaticRetry) {
						retryReason = requestError.retryReason;
						retryDelayMs += Math.max(0, retryNotBefore - Date.now());
						setRetryState(true);
						cancelRetryTimer = scheduleAfter(retryNotBefore, () => {
							setRetryState(false);
							runRequest(false);
						});
						return;
					}
					reportFailure(
						rateLimited
							? 'rateLimited'
							: caught instanceof AtAGlanceRequestError
								? 'httpError'
								: 'networkError',
						toError(caught),
						caught instanceof AtAGlanceRequestError ? caught.status : undefined,
					);
				});
		};
		runRequest(true);

		return () => {
			cancelled = true;
			clearRequestTimers();
			controller.abort();
			if (!settled) {
				createEvent(
					getAtAGlanceAbortedEvent({
						incomingLinkCount,
						outgoingLinkCount,
						durationMs: elapsedMs(),
						...recoveryAttributes(retryCount ? 'cancelledAfterRetry' : 'cancelledBeforeRetry'),
					}),
				).fire();
			}
		};
	}, [cloudId, contentId, createEvent, requestBody]);

	const waitingOnPrereqs = Boolean(
		contentId &&
		cloudId &&
		(!pageAdf ||
			pageTitle === undefined ||
			connections.appearsIn.loading ||
			outgoingLinksLoading ||
			relatedContentLoading),
	);

	const loading = Boolean(enabled && !output && !error && (waitingOnPrereqs || requestBody));
	const retryPending = cachedOutput ? false : currentResultState?.retryPending;
	const retryReason = retryPending ? currentResultState?.retryReason : undefined;
	return useMemo(
		() => ({ output, error, loading, retryPending, retryReason }),
		[output, error, loading, retryPending, retryReason],
	);
};
